/**
 * Product PDF Download Handler (Ceraudo Dynamic Spec Sheet Generator)
 * Uses an isolated sandbox iframe + html2pdf.js to completely prevent
 * Shopify Dawn custom element cloning collisions and CORS canvas issues.
 */

(function () {
  'use strict';

  // Helper: Fetch image URL and convert to Base64 to guarantee 0 canvas tainting
  async function fetchImageAsBase64(url) {
    if (!url) return null;
    try {
      let fullUrl = url;
      if (fullUrl.startsWith('//')) {
        fullUrl = window.location.protocol + fullUrl;
      }
      const response = await fetch(fullUrl, { mode: 'cors' });
      if (!response.ok) return null;
      const blob = await response.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
    } catch (e) {
      console.warn('PDF Generator: Base64 pre-fetch fallback for', url, e);
      return null;
    }
  }

  // Helper: Resolve once a <link rel="stylesheet"> has loaded, errored, or timed out
  function waitForStylesheet(link, timeout) {
    return new Promise((resolve) => {
      if (!link || link.sheet) return resolve();
      link.addEventListener('load', () => resolve(), { once: true });
      link.addEventListener('error', () => resolve(), { once: true });
      setTimeout(resolve, timeout);
    });
  }

  // Helper: Clean filename
  function sanitizeFilename(name) {
    return (name || 'Product')
      .replace(/[^a-zA-Z0-9_\- ]/g, '')
      .trim()
      .replace(/\s+/g, '-');
  }

  // Helper: Escape HTML special characters for text fields
  function escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  // Helper: Sanitize product description HTML (remove iframes, scripts, video)
  function sanitizeDescription(html) {
    if (!html) return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    div.querySelectorAll('script, style, iframe, video, audio, noscript').forEach((el) => el.remove());
    return div.innerHTML;
  }

  // Main PDF Generation Controller
  async function handlePdfDownload(button) {
    const productId = button.getAttribute('data-product-id');
    const dataElement = document.getElementById('ProductPdfData-' + productId);

    if (!dataElement) {
      console.error('PDF Generator: Product JSON data script not found for ID:', productId);
      alert('Could not find product data.');
      return;
    }

    let productData = null;
    try {
      productData = JSON.parse(dataElement.textContent);
    } catch (e) {
      console.error('PDF Generator: Failed to parse product data JSON:', e);
      alert('Invalid product data.');
      return;
    }

    const btnText = button.querySelector('.pdf-btn-text');
    const btnSpinner = button.querySelector('.pdf-btn-spinner');
    const originalText = btnText ? btnText.textContent : 'Download PDF';

    // 1. Set Loading UI State
    button.disabled = true;
    button.classList.add('is-loading');
    if (btnText) btnText.style.display = 'none';
    if (btnSpinner) btnSpinner.style.display = 'inline-flex';

    let iframe = null;

    try {
      // 2. Pre-fetch images to Base64 (0 CORS / 0 Taint guarantee)
      const [firstImgBase64, dimensionsImgBase64, logoBase64] = await Promise.all([
        fetchImageAsBase64(productData.firstImageUrl),
        fetchImageAsBase64(productData.dimensionsImageUrl),
        fetchImageAsBase64(productData.shopLogoUrl)
      ]);

      const firstImgSrc = firstImgBase64 || productData.firstImageUrl;
      const dimensionsImgSrc = dimensionsImgBase64 || productData.dimensionsImageUrl;
      const logoSrc = logoBase64 || productData.shopLogoUrl;

      // 3. Construct Isolated HTML Template (Clean pure HTML with ZERO custom elements)
      const sanitizedDesc = sanitizeDescription(productData.description);
      const cleanTitle = sanitizeFilename(productData.title);
      const html2pdfUrl = button.getAttribute('data-library-url') || '';

      const isolatedHtml = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <title>${escapeHtml(productData.title)} - Spec Sheet</title>
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link id="pdf-assistant-font" rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Assistant:wght@400;500;600;700&display=swap">
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    html, body {
      margin: 0 !important;
      padding: 0 !important;
      background: #ffffff !important;
      overflow-x: hidden !important;
      width: 100% !important;
    }
    #pdf-root-container {
      font-family: 'Assistant', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
      color: #0F3D3D;
      background: #ffffff;
      width: 710px; /* Safe printable width - provides clearance to eliminate right-edge clipping */
      margin: 0 auto;
      padding: 24px 28px;
      font-size: 13px;
      line-height: 1.6;
      box-sizing: border-box;
      -webkit-font-smoothing: antialiased;
    }
    
    /* Header */
    .pdf-header {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      text-align: center;
      border-bottom: 2px solid #0F3D3D;
      padding-bottom: 14px;
      margin-bottom: 22px;
      width: 100%;
    }
    .pdf-brand {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      max-width: 100%;
      margin-bottom: 10px;
    }
    .pdf-logo {
      max-width: 204px; /* +20% (was 170px) */
      max-height: 55px; /* +20% (was 46px) */
      object-fit: contain;
      display: block;
      margin: 0 auto;
    }
    .pdf-brand-name {
      font-size: 22px;
      font-weight: 700;
      letter-spacing: 1.5px;
      text-transform: uppercase;
      color: #0F3D3D;
    }
    .pdf-header-meta {
      text-align: center;
      max-width: 100%;
    }
    .pdf-sheet-tag {
      font-size: 11px;
      font-weight: 600;
      letter-spacing: 1px;
      text-transform: uppercase;
      color: #0F3D3D;
      margin-bottom: 4px;
      white-space: nowrap;
    }
    .pdf-date {
      font-size: 10px;
      color: #0F3D3D;
      white-space: nowrap;
    }

    /* 1. Product First Image */
    .pdf-featured-box {
      width: 100%;
      margin: 0 0 20px 0;
      padding: 0;
      border: 0;
      background: none;
    }
    .pdf-featured-img {
      width: 100%;
      max-width: 100%;
      max-height: 900px; /* safety cap so a very tall image never exceeds one page */
      height: auto;
      object-fit: contain;
      display: block;
      margin: 0;
    }

    /* 2. Product Title & Meta */
    .pdf-product-info {
      margin-bottom: 18px;
      padding-bottom: 14px;
      border-bottom: 1px solid #e0e0e0;
    }
    .pdf-product-title {
      font-size: 24px;
      font-weight: 600;
      line-height: 1.25;
      color: #0F3D3D;
      margin-bottom: 8px;
    }
    .pdf-product-submeta {
      display: flex;
      gap: 16px;
      font-size: 11px;
      color: #0F3D3D;
      text-transform: uppercase;
      letter-spacing: 0.8px;
    }
    .pdf-product-submeta strong {
      color: #0F3D3D;
    }

    /* Headings */
    .pdf-section-heading {
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 1.4px;
      text-transform: uppercase;
      color: #0F3D3D;
      margin: 22px 0 10px 0;
      padding-bottom: 6px;
      border-bottom: 1px solid #0F3D3D;
    }

    /* 3. Product Description */
    .pdf-description-section {
      margin-bottom: 22px;
    }
    .pdf-description-content {
      font-size: 12.5px;
      line-height: 1.65;
      color: #0F3D3D;
    }
    .pdf-description-content p {
      margin-bottom: 10px;
    }
    .pdf-description-content ul,
    .pdf-description-content ol {
      margin: 0 0 12px 18px;
    }
    .pdf-description-content li {
      margin-bottom: 4px;
    }
    .pdf-description-content table {
      width: 100%;
      border-collapse: collapse;
      margin: 12px 0;
      font-size: 11px;
    }
    .pdf-description-content th,
    .pdf-description-content td {
      border: 1px solid #dcdcdc;
      padding: 6px 10px;
      text-align: left;
    }
    .pdf-description-content th {
      background-color: #f7f7f7;
      font-weight: 600;
    }
    /* Force brand text color + font even if the description carries inline styles */
    .pdf-description-content,
    .pdf-description-content * {
      color: #0F3D3D !important;
      font-family: 'Assistant', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important;
    }
    .pdf-description-content img {
      max-width: 100%;
      height: auto;
    }

    /* 4. Metafield Dimensions Image */
    .pdf-dimensions-section {
      margin-top: 24px;
      margin-bottom: 24px;
    }
    .pdf-dimensions-box {
      width: 100%;
      margin: 0;
      padding: 0;
      border: 0;
      background: none;
    }
    .pdf-dimensions-img {
      width: 100%;
      max-width: 100%;
      max-height: 900px; /* safety cap so a very tall image never exceeds one page */
      height: auto;
      object-fit: contain;
      display: block;
      margin: 0;
    }

    /* Footer */
    .pdf-footer {
      margin-top: 28px;
      padding-top: 14px;
      border-top: 1px solid #d5d5d5;
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 10px;
      color: #0F3D3D;
      width: 100%;
    }
    .pdf-footer-note {
      max-width: 60%;
    }
    .pdf-footer-contact {
      text-align: right;
      white-space: nowrap;
      padding-right: 6px;
      max-width: 38%;
    }

    /* Page Break Rules */
    .pdf-avoid-break {
      page-break-inside: avoid !important;
      break-inside: avoid !important;
    }
  </style>
</head>
<body>
  <div id="pdf-root-container">
    <!-- Header -->
    <div class="pdf-header pdf-avoid-break">
      <div class="pdf-brand">
        ${logoSrc ? `<img src="${logoSrc}" alt="${escapeHtml(productData.shopName)}" class="pdf-logo">` : `<h2 class="pdf-brand-name">${escapeHtml(productData.shopName)}</h2>`}
      </div>
      <div class="pdf-header-meta">
        <p class="pdf-sheet-tag">Product Specification Sheet</p>
        <p class="pdf-date">${escapeHtml(productData.currentDate)}</p>
      </div>
    </div>

    <!-- 1. Product First Image -->
    ${firstImgSrc ? `
    <div class="pdf-featured-box pdf-avoid-break">
      <img src="${firstImgSrc}" alt="${escapeHtml(productData.title)}" class="pdf-featured-img">
    </div>` : ''}

    <!-- 2. Product Title & Info -->
    <div class="pdf-product-info pdf-avoid-break">
      <h1 class="pdf-product-title">${escapeHtml(productData.title)}</h1>
      <div class="pdf-product-submeta">
        ${productData.sku ? `<span><strong>SKU:</strong> ${escapeHtml(productData.sku)}</span>` : ''}
        ${productData.vendor ? `<span><strong>Vendor:</strong> ${escapeHtml(productData.vendor)}</span>` : ''}
      </div>
    </div>

    <!-- 3. Product Description -->
    ${sanitizedDesc ? `
    <div class="pdf-description-section">
      <h3 class="pdf-section-heading">Description & Details</h3>
      <div class="pdf-description-content">
        ${sanitizedDesc}
      </div>
    </div>` : ''}

    <!-- 4. Metafield Dimensions Image (custom.dimensions_image) -->
    ${dimensionsImgSrc ? `
    <div class="pdf-dimensions-section pdf-avoid-break">
      <h3 class="pdf-section-heading">Dimensions & Specifications</h3>
      <div class="pdf-dimensions-box">
        <img src="${dimensionsImgSrc}" alt="${escapeHtml(productData.title)} Dimensions" class="pdf-dimensions-img">
      </div>
    </div>` : ''}

    <!-- Footer -->
    <div class="pdf-footer pdf-avoid-break">
      <p class="pdf-footer-note">All specifications and dimensions are provided for reference only.</p>
      <p class="pdf-footer-contact">${escapeHtml(productData.shopName)} &bull; ${escapeHtml(productData.shopUrl)}</p>
    </div>
  </div>
</body>
</html>`;

      // 4. Create Sandboxed Iframe (zero connection with storefront custom elements)
      iframe = document.createElement('iframe');
      iframe.style.cssText = 'position:fixed;left:-9999px;top:0;width:820px;min-height:1400px;border:0;opacity:0;pointer-events:none;z-index:-9999;overflow:hidden;';
      document.body.appendChild(iframe);

      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
      iframeDoc.open();
      iframeDoc.write(isolatedHtml);
      iframeDoc.close();

      // 5. Load html2pdf script dynamically inside the isolated iframe
      await new Promise((resolve, reject) => {
        const script = iframeDoc.createElement('script');
        script.src = html2pdfUrl;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load html2pdf script in iframe'));
        iframeDoc.head.appendChild(script);
      });

      // 6. Ensure all images inside the iframe are fully loaded
      const iframeImages = Array.from(iframeDoc.querySelectorAll('img'));
      await Promise.all(
        iframeImages.map((img) => {
          if (img.complete && img.naturalHeight !== 0) return Promise.resolve();
          return new Promise((res) => {
            img.addEventListener('load', res, { once: true });
            img.addEventListener('error', res, { once: true });
            setTimeout(res, 2500); // safety fallback
          });
        })
      );

      // Wait for the Assistant font stylesheet + font files (falls back to system fonts on failure/timeout)
      await waitForStylesheet(iframeDoc.getElementById('pdf-assistant-font'), 4000);
      if (iframeDoc.fonts && iframeDoc.fonts.load) {
        try {
          await Promise.race([
            Promise.all(
              ['400', '500', '600', '700'].map((w) => iframeDoc.fonts.load(w + " 16px 'Assistant'"))
            ),
            new Promise((res) => setTimeout(res, 4000))
          ]);
        } catch (e) {
          console.warn('PDF Generator: Assistant font load failed, using fallback font', e);
        }
      }

      // Wait for fonts ready if supported
      if (iframeDoc.fonts && iframeDoc.fonts.ready) {
        await iframeDoc.fonts.ready;
      }

      // 7. Execute PDF Generation inside the Iframe Window (completely isolated!)
      const targetElement = iframeDoc.getElementById('pdf-root-container');
      const iframeWin = iframe.contentWindow;
      const opt = {
        margin: iframeWin.Array.of(10, 10, 10, 10),
        filename: `${cleanTitle}-Specification-Sheet.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
          scale: 2, // 2x crisp DPI
          useCORS: true,
          logging: false,
          scrollY: 0,
          scrollX: 0,
          windowWidth: 820
        },
        jsPDF: {
          unit: 'mm',
          format: 'a4',
          orientation: 'portrait'
        },
        pagebreak: {
          mode: iframeWin.Array.of('css', 'legacy'),
          avoid: iframeWin.Array.of(
            '.pdf-avoid-break',
            'img',
            '.pdf-section-heading',
            '.pdf-featured-box',
            '.pdf-dimensions-section',
            '.pdf-product-info'
          )
        }
      };

      // Call html2pdf inside the iframe window
      await iframe.contentWindow.html2pdf().set(opt).from(targetElement).save();

      // 8. Success Feedback
      if (btnSpinner) btnSpinner.style.display = 'none';
      if (btnText) {
        btnText.textContent = '✓ Spec Sheet Downloaded!';
        btnText.style.display = 'inline';
      }
      button.classList.remove('is-loading');
      button.classList.add('is-success');

      setTimeout(() => {
        if (btnText) btnText.textContent = originalText;
        button.classList.remove('is-success');
        button.disabled = false;
      }, 3000);

    } catch (err) {
      console.error('PDF Generator: Error occurred during generation:', err);
      if (btnSpinner) btnSpinner.style.display = 'none';
      if (btnText) {
        btnText.textContent = 'Error Generating PDF';
        btnText.style.display = 'inline';
      }
      button.classList.remove('is-loading');

      setTimeout(() => {
        if (btnText) btnText.textContent = originalText;
        button.disabled = false;
      }, 3000);
    } finally {
      // 9. Clean up iframe from DOM
      if (iframe && iframe.parentNode) {
        // Small delay so download trigger finishes gracefully
        setTimeout(() => {
          if (iframe && iframe.parentNode) {
            iframe.parentNode.removeChild(iframe);
          }
        }, 2000);
      }
    }
  }

  // Global click delegate for download button
  document.addEventListener('click', function (e) {
    const button = e.target.closest('.product-pdf-btn');
    if (button && !button.disabled) {
      e.preventDefault();
      handlePdfDownload(button);
    }
  });

})();
